import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  OrderPrice,
  PriceScope,
  Shipment,
  ShipmentStatus,
} from "@api/graphql/generated/generated-types";
import { Disclosure } from "@headlessui/react";
import {
  ChevronUpIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@src/common/hooks/useAuth";
import { useOrderIdentifierColumnType } from "@src/shipments/hooks/useOrderIdentifierColumnType";
import { resolveOrderIdentifier } from "@src/shipments/utils/orderIdentifierColumn";
import { OrdersPriceForm } from "./components/OrdersPriceForm";
import { Tooltip } from "flowbite-react";
import CourieButton from "../../Button/Button";
import { useRouter } from "next/router";
import OverridePricingDetailForm from "./components/OverridePricingDetailForm";
import { Loading } from "../../Loading";
import PricingBreakDownTable from "./components/PricingBreakDownTable/PricingBreakDownTable";
import DriverCompensation from "./components/DriverCompensation/DriverCompensation";
import ReplaceAllocationView from "../../ReplaceAllocationView/ReplaceAllocationView";
import { ShipmentDistanceModal } from "./components/ShipmentDistanceModal";
import { NotBillablePanel } from "./components/NotBillablePanel";
import { isShipmentNotBilled } from "./utils/isShipmentNotBilled";
import {
  PAGES_WITH_CUSTOM_PRICING,
  PAGES_WITH_DRIVER_PAY,
} from "@src/common/constants/pageGroups";

// Each order card mounts its own Apollo hooks (e.g. the EIA fuel rate
// query), so rendering thousands at once (large invoices) freezes the browser.
const INITIAL_VISIBLE_ORDER_CARDS = 50;
const VISIBLE_ORDER_CARDS_STEP = 50;

type Props = {
  selectedShipments: Shipment[] | null | undefined;
  ordersPrice: OrderPrice[] | undefined;
  onModifyOrderPricing?: () => void;
  key?: number;
  loading?: boolean;
  refetchGetOrderPrice?: () => void;
  /** Rendered at the top of the price card, above the header. */
  banner?: React.ReactNode;
};

function OrdersPriceWidget({
  selectedShipments,
  ordersPrice,
  onModifyOrderPricing,
  key,
  loading = false,
  refetchGetOrderPrice,
  banner,
}: Props) {
  //contexts
  const { pathname } = useRouter();
  const { hasPermission } = useAuth();
  const orderIdentifierColumnType = useOrderIdentifierColumnType();
  const getOrderNumber = (shipment: Shipment | null | undefined) =>
    resolveOrderIdentifier(
      {
        trackingNumber: shipment?.trackingNumber,
        displayId: shipment?.order?.displayId,
      },
      orderIdentifierColumnType,
    );
  // states
  const [driverPayTotal, setDriverPayTotal] = useState<number | undefined>(0);
  const [selectedShipmentForDistance, setSelectedShipmentForDistance] =
    useState<Shipment | null>(null);

  const selectedShipment = selectedShipments && selectedShipments[0];
  const selectedSingleShipmentMemo = useMemo(
    () => selectedShipments && selectedShipments[0],
    [
      selectedShipment?.order.displayId,
      selectedShipment?.trackingNumber,
      selectedShipment?.order,
      selectedShipment?.waitTimeInMinutes,
    ],
  );
  const [forceRefresh, setForceRefresh] = useState(0);
  const [visibleOrderCardCount, setVisibleOrderCardCount] = useState(
    INITIAL_VISIBLE_ORDER_CARDS,
  );
  // A shipment belongs to one invoice, so first/last id + count identifies the
  // shown set without changing on the 10s shipments poll.
  const shownOrdersKey = `${selectedShipments?.[0]?.id}|${
    selectedShipments?.[selectedShipments.length - 1]?.id
  }|${ordersPrice?.length}`;
  const [lastShownOrdersKey, setLastShownOrdersKey] = useState(shownOrdersKey);
  // Reset during render (not in an effect) so a new selection never renders
  // the previously expanded number of cards first.
  let visibleCardLimit = visibleOrderCardCount;
  if (lastShownOrdersKey !== shownOrdersKey) {
    setLastShownOrdersKey(shownOrdersKey);
    setVisibleOrderCardCount(INITIAL_VISIBLE_ORDER_CARDS);
    visibleCardLimit = INITIAL_VISIBLE_ORDER_CARDS;
  }

  // effects
  useEffect(() => {
    if (!hasPermission("orderPricing.view")) return;
    refetchGetOrderPrice && refetchGetOrderPrice();
  }, [key]);

  const renderOrderNumber = (index) => {
    if (
      selectedShipments &&
      selectedShipments?.length === 1 &&
      selectedShipments[0] !== undefined
    ) {
      return `${getOrderNumber(selectedShipments[0])}`;
    } else if (
      selectedShipments &&
      selectedShipments?.length > 1 &&
      selectedShipments[index] !== undefined
    ) {
      return `${getOrderNumber(selectedShipments[index])}`;
    }
    return "";
  };

  const getShipmentFromMultipleShipments = (index) => {
    if (
      selectedShipments &&
      selectedShipments?.length === 1 &&
      selectedShipments[0] !== undefined
    ) {
      return selectedShipments[0];
    } else if (
      selectedShipments &&
      selectedShipments?.length > 1 &&
      selectedShipments[index] !== undefined
    ) {
      return selectedShipments[index];
    }
    return undefined;
  };

  const waitTimeInMinutes =
    selectedSingleShipmentMemo?.waitTimeInMinutes !== undefined;
  const driverPayAllocations = selectedSingleShipmentMemo?.driverPayAllocations;

  const showDriverCompensation =
    PAGES_WITH_DRIVER_PAY.includes(pathname) &&
    selectedSingleShipmentMemo?.status !== ShipmentStatus.Created;

  // A non-billable order is billed $0 on the original order instead, so its price is
  // shown as "not billed" rather than an editable breakdown. Driver pay is unaffected.
  const areAllSelectedNotBilled =
    !!selectedShipments?.length && selectedShipments.every(isShipmentNotBilled);
  const isSingleSelectedNotBilled = isShipmentNotBilled(selectedSingleShipmentMemo);

  const refreshPricing = () => {
    onModifyOrderPricing && onModifyOrderPricing();
    refetchGetOrderPrice && refetchGetOrderPrice();
  };

  function renderOrderPriceBody(orderPrice: OrderPrice, index: number) {
    const shipment = getShipmentFromMultipleShipments(index);
    if (isShipmentNotBilled(shipment)) return <NotBillablePanel />;
    if (orderPrice.isFinalized)
      return (
        <PricingBreakDownTable
          pricingBreakDown={orderPrice.pricingBreakDown}
          total={orderPrice.total}
          orderId={shipment?.order.id || ""}
          onFinalizationChange={refreshPricing}
          taxBreakDown={orderPrice.taxBreakDown}
          totalAfterTax={orderPrice.totalAfterTax}
        />
      );
    return (
      <OrdersPriceForm
        onModifyOrderPricing={refreshPricing}
        selectedShipment={shipment}
        orderPrice={orderPrice}
      />
    );
  }

  const handleAllocationConfirm = useCallback(() => {
    setForceRefresh((prev) => prev + 1);
    refetchGetOrderPrice && refetchGetOrderPrice();
    onModifyOrderPricing && onModifyOrderPricing();
  }, [refetchGetOrderPrice, onModifyOrderPricing]);

  if (!hasPermission("orderPricing.view")) {
    return (
      <div className="p-3 h-full border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-2xl">
        <div className="font-semibold dark:text-gray-100 mb-2">Price</div>
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
          <LockClosedIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You don&apos;t have permission to view order pricing.
          </p>
        </div>
      </div>
    );
  }

  if (ordersPrice && ordersPrice.length > 0) {
    return (
      <Loading
        loading={loading}
        className="p-3 h-full border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-2xl overflow-auto"
      >
        {banner}
        <div className="flex items-center gap-2 mb-2">
          <span className="font-semibold dark:text-gray-100">Price</span>
          {areAllSelectedNotBilled && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              Not billed
            </span>
          )}
          <Tooltip content="View trip distances">
            <button
              onClick={() =>
                setSelectedShipmentForDistance(selectedShipments?.[0] || null)
              }
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <InformationCircleIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </Tooltip>
        </div>
        {ordersPrice.slice(0, visibleCardLimit).map((orderPrice, index) => (
          <div
            className="rounded-lg mb-2 border dark:border-gray-700 shadow-md dark:bg-gray-700"
            key={index}
          >
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full justify-between rounded-lg bg-slate-100 dark:bg-gray-600 px-4 py-2 text-left text-sm font-medium text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-gray-500 focus:outline-none focus-visible:ring focus-visible:ring-slate-500/75">
                    <span>Order {renderOrderNumber(index)}</span>
                    <ChevronUpIcon
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-5 w-5 text-slate-500 dark:text-gray-300`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-1 pb-2 pt-1 text-sm text-gray-500 dark:text-gray-400">
                    {renderOrderPriceBody(orderPrice, index)}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        ))}
        {ordersPrice.length > visibleCardLimit && (
          <div className="flex flex-col items-center gap-1 mb-2">
            <span className="text-xs text-slate-500 dark:text-gray-400">
              Showing {visibleCardLimit} of {ordersPrice.length} orders
            </span>
            <CourieButton
              color="gray"
              size="xs"
              onClick={() =>
                setVisibleOrderCardCount((count) => count + VISIBLE_ORDER_CARDS_STEP)
              }
            >
              Show more
            </CourieButton>
          </div>
        )}
        {PAGES_WITH_CUSTOM_PRICING.includes(pathname) &&
          !isSingleSelectedNotBilled &&
          waitTimeInMinutes &&
          ordersPrice &&
          !ordersPrice[0].isFinalized && (
            <>
              <span className="font-semibold dark:text-gray-100">
                Custom Pricing
              </span>
              <div className="rounded-lg mb-2 border dark:border-gray-700 shadow-md dark:bg-gray-700">
                <Disclosure defaultOpen>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full justify-between rounded-lg bg-slate-100 dark:bg-gray-600 px-4 py-2 text-left text-sm font-medium text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-gray-500 focus:outline-none focus-visible:ring focus-visible:ring-slate-500/75">
                        <span>
                          Order {getOrderNumber(selectedSingleShipmentMemo)}
                        </span>
                        <ChevronUpIcon
                          className={`${
                            open ? "rotate-180 transform" : ""
                          } h-5 w-5 text-slate-500 dark:text-gray-300`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-1 pb-2 pt-1 text-sm text-gray-500 dark:text-gray-400">
                        <OverridePricingDetailForm
                          shipment={selectedSingleShipmentMemo}
                          onOveridePrice={() => {
                            refetchGetOrderPrice && refetchGetOrderPrice();
                          }}
                          orderPrice={ordersPrice[0]}
                        />
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            </>
          )}
        {showDriverCompensation && (
          <DriverCompensation
            selectedSingleShipmentMemo={selectedSingleShipmentMemo}
            setDriverPayTotal={setDriverPayTotal}
            driverPayTotal={driverPayTotal}
            key={`driver-comp-${forceRefresh}`}
          />
        )}
        {showDriverCompensation && (
          <>
            <span className="font-semibold dark:text-gray-100">
              Driver Allocations
            </span>
            <div className="rounded-lg mb-2 border dark:border-gray-700 shadow-md dark:bg-gray-700">
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-slate-100 dark:bg-gray-600 px-4 py-2 text-left text-sm font-medium text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-gray-500 focus:outline-none focus-visible:ring focus-visible:ring-slate-500/75">
                      <span>
                        Order {getOrderNumber(selectedSingleShipmentMemo)}
                      </span>
                      <ChevronUpIcon
                        className={`${
                          open ? "rotate-180 transform" : ""
                        } h-5 w-5 text-slate-500 dark:text-gray-300`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-1 pb-2 pt-1 text-sm text-gray-500 dark:text-gray-400">
                      <ReplaceAllocationView
                        driverPayAllocations={driverPayAllocations}
                        shipmentId={selectedSingleShipmentMemo?.id}
                        onConfirm={handleAllocationConfirm}
                      />
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          </>
        )}
        {selectedShipmentForDistance && (
          <ShipmentDistanceModal
            isOpen={true}
            onClose={() => setSelectedShipmentForDistance(null)}
            shipment={selectedShipmentForDistance}
            isWholeOrderPricing={ordersPrice?.[0]?.tripPrices.some((t) => t.scope === PriceScope.PerOrder) ?? false}
            onMilesOverrideSaved={() => {
              refetchGetOrderPrice?.();
              onModifyOrderPricing?.();
            }}
          />
        )}
      </Loading>
    );
  } else {
    return (
      <Loading
        className="p-3 h-full border dark:border-gray-700 bg-white dark:bg-gray-800 rounded-2xl"
        loading={loading}
      ></Loading>
    );
  }
}

export default OrdersPriceWidget;
